/**
 * AlexDock — flytande, alltid tillgänglig Alex-chat (alla vyer).
 * Apple-känsla: glaspanel, spring-animation, ⌘J togglar, Esc stänger.
 * Delar huvudagent och tråd med Alex-vyn. Håll-och-prata-knappen
 * går genom exakt samma sendText: rösten är ett annat tangentbord, inte en annan Alex.
 * Monteras på App-nivå så konversationen överlever vy-byten.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CollapsibleMarkdown } from './chat/CollapsibleMarkdown';
import { Mic, X, ArrowUp, Square, Minus } from 'lucide-react';
import { MAIN_SESSION, useAlexGateway } from '../gateway/AlexGateway';
import { isNoiseMessage } from './chat/messageNoise';
import { withVoiceMarker, stripVoiceMarker } from './chat/voiceMarker';
import { useWalkieTalkie, TALK_LABEL } from '../hooks/useWalkieTalkie';
import { presenter, type PresenterState, type PresentStep } from '../voice/presenter';
import { speechQueue } from '../voice/speechQueue';
import '../styles/alexdock.css';

interface ChatMsg {
    role: 'user' | 'assistant';
    content: string;
    /** Rent skärmkommando ("visa …"): visas som en diskret notis, läses aldrig upp. */
    note?: boolean;
}

const SUGGESTIONS = [
    'Var i pipelinen är Skindiver?',
    'Lista tier A-prospekten',
    'Vilka sekvenser är aktiva?',
];

export function AlexDock() {
    const [open, setOpen] = useState(false);
    // Minimerat = panelen krymper till en rad. Komponenten är kvar monterad,
    // så strömmande svar och röst fortsätter medan skärmen är fri.
    const [minimized, setMinimized] = useState(false);
    const gateway = useAlexGateway();
    const connected = gateway.status === 'connected';
    const busy = gateway.isStreaming;
    const [notes, setNotes] = useState<ChatMsg[]>([]);
    const messages = gateway.messages.filter(m => !isNoiseMessage(m));
    const spokenRef = useRef<{ session: string; from: number } | null>(null);
    const [input, setInput] = useState('');
    const scrollRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);


    // Öppnas från Alex-knappen i navigeringsklustret (scc:open-alex)
    useEffect(() => {
        const onOpenAlex = () => { setMinimized(false); setOpen(true); };
        window.addEventListener('scc:open-alex', onOpenAlex);
        return () => window.removeEventListener('scc:open-alex', onOpenAlex);
    }, []);

    // ⌘J / Ctrl+J togglar, Esc stänger
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
                e.preventDefault();
                setOpen((o) => { if (o) setMinimized(false); return !o; });
            } else if (e.key === 'Escape') {
                presenter.stop();
                speechQueue.stop();
                setOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => {
        if (open) inputRef.current?.focus();
    }, [open]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }, [gateway.messages, gateway.streamingContent, notes, busy]);

    const sendText = useCallback(
        async (text: string, channel: 'chat' | 'voice' = 'chat'): Promise<null> => {
            if (!text.trim() || busy || !connected) return null;
            presenter.stop();
            speechQueue.stop();
            setInput('');
            spokenRef.current = channel === 'voice'
                ? { session: gateway.sessionKey, from: gateway.messages.length } : null;
            gateway.sendMessage(channel === 'voice' ? withVoiceMarker(text) : text);
            return null;
        },
        [busy, connected, gateway],
    );

    // Håll-och-prata: transkriptet går in i sendText ovan, svaret läses upp.
    const talk = useWalkieTalkie({ onTranscript: async (t) => { await sendText(t, 'voice'); return null; } });
    useEffect(() => {
        const request = spokenRef.current;
        if (!request) return;
        if (!connected || request.session !== gateway.sessionKey) {
            spokenRef.current = null;
            return;
        }
        if (busy) return;
        const answer = gateway.messages.slice(request.from).find(m => m.role === 'assistant');
        if (!answer) return;
        spokenRef.current = null;
        void talk.speak(answer.content);
    }, [busy, connected, gateway.messages, gateway.sessionKey, talk]);

    const [queueSpeaking, setQueueSpeaking] = useState(false);
    useEffect(() => speechQueue.onState(setQueueSpeaking), []);

    // Alex egen genomgång (present_screens): stegen spelas upp av presentern,
    // varje stegtext landar dessutom som notis i tråden när den läses upp.
    const [show, setShow] = useState<PresenterState | null>(null);
    useEffect(() => presenter.subscribe(setShow), []);
    useEffect(() => {
        const onPresent = (e: Event) => {
            const steps = (e as CustomEvent<{ steps: PresentStep[] }>).detail?.steps ?? [];
            if (!steps.length) return;
            talk.hush();
            setOpen(true);
            presenter.start(steps);
        };
        window.addEventListener('scc:present', onPresent);
        return () => window.removeEventListener('scc:present', onPresent);
    }, [talk]);
    // Rader som kommer utan att du frågat. Två sorter, samma rör:
    //   'delegate' — svaret på något du faktiskt bett om, läses upp.
    //   'pulse'    — Alex säger till självmant. Läses BARA upp om du varit
    //                aktiv nyligen och inget annat låter. Ett tomt rum ska
    //                inte pratas till, och en pågående genomgång inte brytas.
    const lastActiveRef = useRef<number>(Date.now());
    useEffect(() => {
        const touch = () => { lastActiveRef.current = Date.now(); };
        window.addEventListener('pointerdown', touch);
        window.addEventListener('keydown', touch);
        return () => {
            window.removeEventListener('pointerdown', touch);
            window.removeEventListener('keydown', touch);
        };
    }, []);

    useEffect(() => {
        const onNote = (e: Event) => {
            const d = (e as CustomEvent<{ text: string; speech: string; speak: boolean; source: string }>).detail;
            if (!d?.text) return;
            const selfInitiated = d.source === 'pulse';
            // Självmant: visa i tråden men öppna inte panelen över det du gör.
            if (!selfInitiated) setOpen(true);
            setNotes((prev) => [...prev, { role: 'assistant', content: d.text, note: selfInitiated }]);

            const busyTalking = presenter.current !== null || speechQueue.speaking || talk.state !== 'idle';
            const recentlyActive = Date.now() - lastActiveRef.current < 15 * 60_000;
            const maySpeak = d.speak && !busyTalking && (!selfInitiated || (recentlyActive && open));
            if (maySpeak) void talk.speak(d.speech || d.text);
        };
        window.addEventListener('scc:alex-note', onNote);
        return () => window.removeEventListener('scc:alex-note', onNote);
    }, [talk, open]);

    const lastNotedRef = useRef<string>('');
    useEffect(() => {
        if (!show || show.status === 'done') return;
        const key = `${show.index}:${show.steps.length}`;
        if (lastNotedRef.current === key) return;
        lastNotedRef.current = key;
        const step = show.steps[show.index];
        const where = step.customer_name ?? step.contact_name ?? step.view ?? '';
        setNotes((prev) => [...prev, { role: 'assistant', content: `${where ? where + ': ' : ''}${step.say}`, note: true }]);
    }, [show]);
    const talking = talk.state !== 'idle';

    // Håll mellanslag = håll in knappen. Fungerar även när textfältet har
    // fokus (panelen fokuserar det vid öppning), så länge fältet är tomt.
    useEffect(() => {
        if (!open || !talk.supported || !connected || busy) return;
        const down = (e: KeyboardEvent) => {
            if (e.code !== 'Space' || e.repeat) return;
            const el = document.activeElement;
            const inField = el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
            if (inField && el !== inputRef.current) return;
            if (el === inputRef.current && inputRef.current?.value) return;
            e.preventDefault();
            presenter.stop();
            speechQueue.stop();
            talk.hush();
            void talk.start();
        };
        const up = (e: KeyboardEvent) => {
            if (e.code === 'Space') talk.stop();
        };
        window.addEventListener('keydown', down);
        window.addEventListener('keyup', up);
        return () => {
            window.removeEventListener('keydown', down);
            window.removeEventListener('keyup', up);
        };
    }, [open, talk, connected, busy]);

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();
        sendText(input.trim());
    };

    return (
        <>
            {/* Ingen egen flytande knapp längre — Alex öppnas från ✦ i
                navigeringsklustret (ett dragbart objekt, inte två). */}

            {/* Panel */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        key="panel"
                        className={`alexdock-panel${minimized ? ' alexdock-panel--min' : ''}`}
                        initial={{ opacity: 0, y: 24, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.97 }}
                        transition={{ type: 'spring', stiffness: 340, damping: 30 }}
                    >
                        {/* Header */}
                        <div
                            className="alexdock-header"
                            onClick={() => { if (minimized) setMinimized(false); }}
                            style={minimized ? { cursor: 'pointer', borderBottom: 'none' } : undefined}
                        >
                            <div className="alexdock-header-title">
                                <span className="alexdock-status-dot" style={connected ? undefined : { background: '#f87171' }} />
                                <span>Alex</span>
                                <span className="alexdock-header-sub">
                                    {!minimized
                                        ? (connected ? (gateway.sessionKey === MAIN_SESSION ? 'huvudtråd · samma som WhatsApp' : 'separat tråd · samma som Alex-vyn') : 'Alex är inte ansluten')
                                        : talk.state === 'recording'
                                            ? 'lyssnar…'
                                            : queueSpeaking || talk.state === 'speaking'
                                                ? 'pratar…'
                                                : busy || talking
                                                    ? 'jobbar…'
                                                    : 'minimerad · klicka för att öppna'}
                                </span>
                            </div>
                            <div className="alexdock-header-actions">
                                <button
                                    className="alexdock-icon-btn"
                                    onClick={(e) => { e.stopPropagation(); setMinimized((m) => !m); }}
                                    title={minimized ? 'Visa chatten igen' : 'Minimera (Alex fortsätter prata)'}
                                >
                                    {minimized ? <Mic size={15} /> : <Minus size={15} />}
                                </button>
                                <button
                                    className="alexdock-icon-btn"
                                    onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                                    title="Stäng (Esc)"
                                >
                                    <X size={15} />
                                </button>
                            </div>
                        </div>

                        {/* Innehåll — dolt när panelen är minimerad */}
                        {!minimized && (
                        <>
                                <div ref={scrollRef} className="alexdock-messages">
                                    {!connected && <p className="alex-thread-note" role="status">
                                        Alex kan inte nås. Kontrollera Tailscale och serveranslutningen.
                                        Din historik finns kvar.
                                    </p>}
                                    {messages.length === 0 && !busy && connected && (
                                        <div className="alexdock-empty">
                                            <p>Vad vill du veta?</p>
                                            <div className="alexdock-suggestions">
                                                {SUGGESTIONS.map((s) => (
                                                    <button key={s} onClick={() => sendText(s)}>
                                                        {s}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {messages.map((m, i) => (
                                        <div
                                            key={i}
                                            className={`alexdock-msg ${m.role === 'user' ? 'alexdock-msg--user' : 'alexdock-msg--assistant'}`}
                                        >
                                            {m.role === 'assistant' ? (
                                                <CollapsibleMarkdown content={m.content} />
                                            ) : (
                                                stripVoiceMarker(m.content)
                                            )}
                                        </div>
                                    ))}
                                    {gateway.streamingContent && (
                                        <div className="alexdock-msg alexdock-msg--assistant">
                                            <CollapsibleMarkdown content={gateway.streamingContent} />
                                        </div>
                                    )}
                                    {notes.map((m, i) => <div key={`note-${i}`} className="alexdock-msg alexdock-msg--note">
                                        {m.content}
                                    </div>)}
                                    {(busy || talking || show || queueSpeaking) && (
                                        <div className={`alexdock-thinking ${talk.state === 'recording' ? 'alexdock-thinking--rec' : ''}`}>
                                            {queueSpeaking && !show
                                                ? 'Alex pratar…'
                                                : talking
                                                ? TALK_LABEL[talk.state]
                                                : show
                                                    ? (show.status === 'done' ? 'Klart.' : `Alex visar runt (${show.index + 1}/${show.steps.length}) · skriv eller tryck på mikrofonen för att avbryta`)
                                                    : 'Alex tänker…'}
                                        </div>
                                    )}
                                    {talk.error && (
                                        <div className="alexdock-thinking alexdock-thinking--err" onClick={talk.clearError}>
                                            ⚠️ {talk.error}
                                        </div>
                                    )}
                                </div>

                                <form onSubmit={onSubmit} className="alexdock-inputrow">
                                    <input
                                        ref={inputRef}
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        placeholder="Fråga Alex…"
                                    />
                                    {talk.supported && (
                                        <button
                                            type="button"
                                            className={`alexdock-mic alexdock-mic--${queueSpeaking && talk.state === 'idle' ? 'speaking' : talk.state}`}
                                            disabled={!connected || (busy && talk.state === 'idle')}
                                            title={talk.state === 'speaking' || queueSpeaking ? 'Tysta Alex' : 'Håll in och prata (eller håll mellanslag)'}
                                            onPointerDown={(e) => {
                                                e.preventDefault();
                                                presenter.stop();
                                                if (queueSpeaking) { speechQueue.stop(); return; }
                                                if (talk.state === 'speaking') { talk.hush(); return; }
                                                void talk.start();
                                            }}
                                            onPointerUp={talk.stop}
                                            onPointerLeave={talk.stop}
                                            onPointerCancel={talk.stop}
                                            onContextMenu={(e) => e.preventDefault()}
                                        >
                                            {talk.state === 'speaking' || queueSpeaking ? <Square size={14} strokeWidth={2.5} /> : <Mic size={16} strokeWidth={2.25} />}
                                        </button>
                                    )}
                                    <button type="submit" disabled={!connected || busy || talking || !input.trim()} title="Skicka">
                                        <ArrowUp size={16} strokeWidth={2.5} />
                                    </button>
                                </form>
                        </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
